/**
 * Hook for managing the P2P sending contact queue.
 *
 * Handles: prefetching, skip, navigation, queue exhaustion,
 * auto-A/B script rotation, and personalization.
 */
import { useState, useCallback, useRef, useEffect } from "react";
import api from "../services/api";

export default function useContactQueue({ campaignId, onQueueEmpty }) {
  const [currentContact, setCurrentContact] = useState(null);
  const [currentCC, setCurrentCC] = useState(null);
  const [currentScript, setCurrentScript] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [prefetchQueue, setPrefetchQueue] = useState([]);
  const [queueStats, setQueueStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [queueExhausted, setQueueExhausted] = useState(false);

  const campaignIdRef = useRef(campaignId);
  campaignIdRef.current = campaignId;

  // Phase 42 audit (F4): pre-fix in-flight requests weren't cancelled
  // when the user navigated away or switched campaigns. The setState
  // calls below would then run on an unmounted component → memory
  // leak + React's "Can't perform a state update on an unmounted
  // component" warning. Worse, on rapid campaign switches the OLDER
  // request could land AFTER the newer one and overwrite the visible
  // state with stale data. We track an AbortController and cancel
  // on unmount or campaign change.
  const abortRef = useRef(null);

  // Load next contact(s) from server
  const loadNext = useCallback(async (prefetch = 5) => {
    if (!campaignIdRef.current) return;

    // Abort any prior in-flight loadNext before starting a new one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const res = await api.get(
        `/messages/next/${campaignIdRef.current}?prefetch=${prefetch}`,
        { signal: controller.signal }
      );

      if (!res.data.contact) {
        setCurrentContact(null);
        setCurrentCC(null);
        setCurrentScript(null);
        setConversationHistory([]);
        setPrefetchQueue([]);
        setQueueExhausted(true);
        onQueueEmpty?.();
        return;
      }

      setQueueExhausted(false);
      setCurrentContact(res.data.contact);
      setCurrentCC(res.data.campaign_contact);
      setCurrentScript(res.data.script);
      setConversationHistory(res.data.history || []);
      setPrefetchQueue(res.data.queue || []);

      if (res.data.queue_stats) {
        setQueueStats(res.data.queue_stats);
      }
    } catch (err) {
      // Ignore aborted-request errors — they're our own cancellation,
      // not a real failure. axios surfaces them as ERR_CANCELED.
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      console.error("Failed to load next contact:", err);
    } finally {
      // Only clear loading if this controller is still the active one.
      // If a newer loadNext aborted us, that newer call now owns the
      // loading state and we shouldn't flip it back.
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [onQueueEmpty]);

  // Skip current contact and advance
  const skipContact = useCallback(async () => {
    if (!currentContact || !campaignIdRef.current) return;

    try {
      await api.post(
        `/messages/skip/${campaignIdRef.current}/${currentContact.id}`
      );

      // Update stats locally
      setQueueStats((prev) =>
        prev
          ? {
              ...prev,
              pending: prev.pending - 1,
              skipped: prev.skipped + 1,
              pct_complete: Math.round(
                ((prev.total - prev.pending + 1) / Math.max(prev.total, 1)) * 1000
              ) / 10,
            }
          : prev
      );

      // Advance to next
      await loadNext();
    } catch (err) {
      console.error("Failed to skip contact:", err);
    }
  }, [currentContact, loadNext]);

  // After a successful send, advance to next contact
  const advanceAfterSend = useCallback(async () => {
    // Update stats locally
    setQueueStats((prev) =>
      prev
        ? {
            ...prev,
            pending: prev.pending - 1,
            sent: prev.sent + 1,
            pct_complete: Math.round(
              ((prev.total - prev.pending + 1) / Math.max(prev.total, 1)) * 1000
            ) / 10,
          }
        : prev
    );

    await loadNext();
  }, [loadNext]);

  // Personalize a script body with contact merge fields
  const personalizeBody = useCallback(
    (body, contact = null) => {
      const c = contact || currentContact;
      if (!c || !body) return body || "";

      return body
        .replace(/\{first_name\}/gi, c.first_name || "")
        .replace(/\{last_name\}/gi, c.last_name || "")
        .replace(/\{phone\}/gi, c.phone_number || "")
        .replace(/\{city\}/gi, c.city || "")
        .replace(/\{state\}/gi, c.state || "")
        .replace(/\{county\}/gi, c.county || "")
        .replace(/\{precinct\}/gi, c.precinct || "")
        .replace(/\{party\}/gi, c.party || "")
        .replace(/\{van_id\}/gi, c.van_id || "");
    },
    [currentContact]
  );

  // Refresh queue stats from server
  const refreshStats = useCallback(async () => {
    if (!campaignIdRef.current) return;
    try {
      const res = await api.get(`/messages/queue-stats/${campaignIdRef.current}`);
      setQueueStats(res.data);
    } catch (err) {
      console.error("Failed to refresh queue stats:", err);
    }
  }, []);

  // Initial load when campaign changes
  useEffect(() => {
    if (campaignId) {
      setQueueExhausted(false);
      loadNext(5);
    } else {
      setCurrentContact(null);
      setCurrentCC(null);
      setCurrentScript(null);
      setConversationHistory([]);
      setPrefetchQueue([]);
      setQueueStats(null);
    }
    // Phase 42 (F4): on unmount or campaign change, cancel any
    // in-flight request the previous campaign started so its setState
    // doesn't fire after the component is gone or stale-overwrite the
    // new campaign's data.
    return () => {
      abortRef.current?.abort();
    };
  }, [campaignId]); // eslint-disable-line

  return {
    currentContact,
    currentCC,
    currentScript,
    conversationHistory,
    prefetchQueue,
    queueStats,
    queueExhausted,
    loading,
    loadNext,
    skipContact,
    advanceAfterSend,
    personalizeBody,
    refreshStats,
  };
}
